import { useEffect, useRef, useState } from "react";
import { mountPhotoHologram } from "@/components/compare/photoHologram";
import { STATUS_LABEL } from "@/lib/compareRules";
import { Cog, CircleDot, Disc, ArrowUpDown, Car, Fuel, Gauge, PoundSterling } from "lucide-react";

const LEFT = [["engine", "Engine and gearbox", Cog], ["tyres", "Tyres", CircleDot], ["brakes", "Brakes", Disc], ["suspension", "Suspension and steering", ArrowUpDown]];
const RIGHT = [["body", "Bodywork and condition", Car], ["running", "Running costs", Fuel], ["history", "Mileage history", Gauge], ["price", "Price and value", PoundSterling]];
const DOT = { good: "#34D399", amber: "#FBBF24", red: "#F87171", grey: "#64748B" };

function Chip({ zone, label, Icon, status, selected, onSelect, show }) {
  const dim = selected && selected !== zone;
  return (
    <button type="button" onClick={() => onSelect(zone)} data-testid={`chip-zone-${zone}`}
      style={{ borderColor: DOT[status], boxShadow: selected === zone ? `0 0 18px ${DOT[status]}` : "none" }}
      className={`w-full flex items-center gap-2 rounded-xl border bg-[#0B1620]/80 backdrop-blur px-3 py-2 text-left transition-opacity duration-500 ${show ? (dim ? "opacity-40" : "opacity-100") : "opacity-0 pointer-events-none"}`}>
      <Icon className="w-4 h-4 shrink-0" style={{ color: DOT[status] }} />
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold text-white truncate">{label}</span>
        <span className="block text-[11px]" style={{ color: DOT[status] }}>{STATUS_LABEL[status]}</span>
      </span>
    </button>
  );
}

export function useChipFade(replay, reduce) {
  const [show, setShow] = useState(reduce);
  useEffect(() => {
    if (reduce) { setShow(true); return undefined; }
    setShow(false);
    const t = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(t);
  }, [replay, reduce]);
  return show;
}

export function usePhotoHologram({ photos, view, replay, reduce, onFail }) {
  const host = useRef(null);
  const ctl = useRef(null);
  const show = useChipFade(replay, reduce);
  useEffect(() => {
    ctl.current = mountPhotoHologram(host.current, { photos, view, reducedMotion: reduce, onError: onFail });
    return () => ctl.current?.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.a, photos.b, reduce, onFail]);
  useEffect(() => { ctl.current?.setView(view); }, [view]);
  useEffect(() => { if (replay) ctl.current?.replay(); }, [replay]);
  return { host, show };
}

export function PartChips({ side, result, view, selected, onSelect, show }) {
  const list = side === "left" ? LEFT : side === "right" ? RIGHT : [...LEFT, ...RIGHT];
  return list.map(([zone, label, Icon]) => (
    <Chip key={zone} zone={zone} label={label} Icon={Icon} status={result.zones[zone].status[view]} selected={selected} onSelect={onSelect} show={show} />
  ));
}
